/**
 * Putting a run in the URL.
 *
 * The determinism guarantee is what makes this worth doing: a scenario id, a seed, and the
 * handful of parameters the sender changed reproduce their run byte for byte. Nothing is
 * stored anywhere, nothing is looked up, and the link works forever as long as the engine
 * version matches.
 *
 * The payload is the override set rather than a flattened config, which is why these links
 * stay short. Overrides only ever contain fields the user actually touched — a default run
 * is about forty characters, and a heavily edited one is still a few hundred rather than
 * the four hundred numbers a full config would need.
 *
 * The engine version travels with it. An old link opened against a newer model would
 * silently replay as something else, and silently is the one thing it must not be.
 */

import {
  ENGINE_VERSION,
  changedTunables,
  type ScenarioOverrides,
} from 'walkout-engine';

export interface SharedRun {
  scenarioId: string;
  overrides: ScenarioOverrides;
  /** Engine version that produced the link, if it carried one. */
  engineVersion: string | null;
}

interface Payload {
  v: string;
  s: string;
  o: ScenarioOverrides;
}

/** base64url, without the padding that makes URLs ugly and sometimes gets mangled. */
function encodeBase64Url(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function decodeBase64Url(text: string): string {
  const padded = text.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='));
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * Drop anything that is not actually an edit, so a link records intent rather than state.
 * A shared run should show the reader what the sender changed.
 */
function compact(overrides: ScenarioOverrides): ScenarioOverrides {
  const out: ScenarioOverrides = { seed: overrides.seed };
  if (overrides.horizonDays !== undefined) out.horizonDays = overrides.horizonDays;

  const nonEmpty = <T extends object>(value: T | undefined): T | undefined =>
    value && Object.keys(value).length > 0 ? value : undefined;

  const movement = nonEmpty(overrides.movement);
  if (movement) out.movement = movement;
  const media = nonEmpty(overrides.media);
  if (media) out.media = media;
  const regime = nonEmpty(overrides.regime);
  if (regime) out.regime = regime;

  const tunables = changedTunables(overrides.tunables);
  if (Object.keys(tunables).length > 0) out.tunables = tunables;

  const sectors = nonEmpty(overrides.sectors);
  if (sectors) out.sectors = sectors;
  const pillars = nonEmpty(overrides.pillars);
  if (pillars) out.pillars = pillars;

  if (overrides.addSectors?.length) out.addSectors = overrides.addSectors;
  if (overrides.addPillars?.length) out.addPillars = overrides.addPillars;
  if (overrides.removeSectors?.length) out.removeSectors = overrides.removeSectors;
  if (overrides.removePillars?.length) out.removePillars = overrides.removePillars;

  return out;
}

export function encodeRun(scenarioId: string, overrides: ScenarioOverrides): string {
  const payload: Payload = { v: ENGINE_VERSION, s: scenarioId, o: compact(overrides) };
  return encodeBase64Url(JSON.stringify(payload));
}

export function decodeRun(encoded: string): SharedRun | null {
  try {
    const parsed = JSON.parse(decodeBase64Url(encoded)) as Partial<Payload>;
    if (typeof parsed.s !== 'string' || !parsed.o || typeof parsed.o.seed !== 'number') {
      return null;
    }
    return {
      scenarioId: parsed.s,
      overrides: parsed.o,
      engineVersion: typeof parsed.v === 'string' ? parsed.v : null,
    };
  } catch {
    // A truncated or hand-edited link is a normal thing to receive, not an error worth
    // showing anyone. Fall back to the default run.
    return null;
  }
}

/** Read a shared run out of the current location, if there is one. */
export function readLocation(): SharedRun | null {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  const run = params.get('r');
  return run ? decodeRun(run) : null;
}

/**
 * Write the current run into the address bar without adding a history entry.
 *
 * `replaceState` rather than `pushState`: parameters change on every drag of a slider, and
 * filling the back button with hundreds of intermediate states would make it useless for
 * the thing people actually want it for.
 */
export function writeLocation(scenarioId: string, overrides: ScenarioOverrides): void {
  const encoded = encodeRun(scenarioId, overrides);
  const url = `${window.location.pathname}${window.location.search}#r=${encoded}`;
  window.history.replaceState(null, '', url);
}

export function shareUrl(scenarioId: string, overrides: ScenarioOverrides): string {
  return `${window.location.origin}${window.location.pathname}#r=${encodeRun(scenarioId, overrides)}`;
}

export function isCurrentEngine(shared: SharedRun): boolean {
  return shared.engineVersion === null || shared.engineVersion === ENGINE_VERSION;
}
